import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { SelfOnboardingSegment } from '@app/enums';
import { AppState } from '@app/reducers';
import { hasAtleastSubscriptionPlan } from '@app/reducers/account/account-subscription.helper';
import { SubscriptionModel } from '@app/reducers/account/account-subscription.model';
import { AccountModel } from '@app/reducers/account/account.model';
import { getAccount, getAccountSubscription, getSelfOnboarding } from '@app/reducers/account/account.service';
import { getUserGuideEntities, UserGuideModel, UserGuideType } from '@app/reducers/orm/user-guide';
import {
  OnboardingChaptersCTA,
  onboardingChaptersCTAByPriority,
  OnboardingChecklistStep,
  OnboardingChecklistStepConfiguration,
  OnboardingStep,
  smbHospitalityOnboardingChecklistStepConfiguration,
} from '@app/shared/onboarding/onboarding-checklist.model';
import { FeatureService } from '@app/startup/feature.service';
import { select, Store } from '@ngrx/store';
import { Dictionary } from 'lodash';
import { combineLatest, filter, Observable } from 'rxjs';

import { getEnhancedTeams } from '../reducers/orm/department/department.selectors';
import {
  EmployeeForBatchPayload,
  EmployeeModel,
  EmployeeTeamSavePayload,
} from '../reducers/orm/employee/employee.model';
import { getActiveEmployees } from '../reducers/orm/employee/employee.service';
import { EnhancedTeamModel } from '../reducers/orm/team/team.model';

@Injectable({
  providedIn: 'root',
})
export class OnboardingUtils {
  public nextPriorityCTA?: OnboardingChaptersCTA;

  private employees$: Observable<EmployeeModel[]> = this.store.select(getActiveEmployees);
  private teams$: Observable<Dictionary<EnhancedTeamModel>> = this.store.select(getEnhancedTeams);
  private subscription$: Observable<SubscriptionModel> = this.store.pipe(select(getAccountSubscription));
  private account$: Observable<AccountModel> = this.store.pipe(select(getAccount));
  private userGuide$: Observable<Dictionary<UserGuideModel>> = this.store.select(getUserGuideEntities);
  private selfOnboardingSegment$: Observable<SelfOnboardingSegment> = this.store.select(getSelfOnboarding);

  private employees: EmployeeModel[] = [];
  private teams: Dictionary<EnhancedTeamModel> = {};
  private subscription: SubscriptionModel;
  private account: AccountModel;

  public constructor(
    private readonly store: Store<AppState>,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly destroyRef: DestroyRef,
    private readonly featureService: FeatureService,
  ) {
    void combineLatest([this.userGuide$, this.selfOnboardingSegment$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([guide, selfOnboarding]) => this.setNextPriorityChapterCTA(guide, selfOnboarding));

    void this.employees$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((employees) => {
      this.employees = employees;
    });

    void this.teams$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((teams) => {
      this.teams = teams;
    });

    void this.subscription$
      .pipe(
        filter((subscription) => !!subscription),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((subscription: SubscriptionModel) => {
        this.subscription = subscription;
      });

    void this.account$
      .pipe(
        filter((account) => !!account),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((account: AccountModel) => {
        this.account = account;
      });
  }

  public setNextPriorityChapterCTA(guide, selfOnboarding) {
    let userGuideType = '';
    if (selfOnboarding) {
      userGuideType = UserGuideType.SELF_ONBOARDING_CHECKLIST;
    }

    if (
      !userGuideType ||
      !guide?.[userGuideType] ||
      guide?.[userGuideType]?.status === 'dismissed' ||
      guide?.[userGuideType]?.status === 'finished'
    ) {
      this.nextPriorityCTA = undefined;
      return;
    }

    let completedSteps = [];
    const selfOnboardingGuide = guide?.[userGuideType];

    if (selfOnboardingGuide?.step) {
      completedSteps = selfOnboardingGuide.step.split(',').map((step) => step.trim() as OnboardingChecklistStep);
    }

    // Filter uncompleted chapters based on completedSteps
    const uncompletedChapters = onboardingChaptersCTAByPriority.filter(
      (chapter) => !completedSteps.includes(chapter.primary.step),
    );

    // Filter chapters that are accessible
    const accessibleChapters = uncompletedChapters.filter((chapter) => this.canAccessStep(chapter.primary.step));

    this.nextPriorityCTA = accessibleChapters[0];
  }

  public stripOnboardingParamsFromUrl(url: string): string {
    // Handle matrix parameters and query parameters separately
    const [basePath, queryString] = url.split('?');

    // Define onboarding parameters to be removed
    const onboardingParams = ['onboardingStep', 'originator'];

    // Remove onboarding matrix parameters from the base path
    // This regex also ensures we don't leave dangling semicolons
    const cleanBasePath = basePath.replace(new RegExp(`;(${onboardingParams.join('|')})=[^;]*`, 'g'), '');

    // If there's no query string, just return the cleaned base path
    if (!queryString) {
      return cleanBasePath;
    }

    // Process the query string to remove onboarding parameters
    const queryParams = queryString.split('&');
    const filteredParams = queryParams.filter((param) => {
      const paramName = param.split('=')[0];
      return !onboardingParams.includes(paramName);
    });

    // Return the cleaned URL with query string only if there are params left
    return filteredParams.length > 0 ? `${cleanBasePath}?${filteredParams.join('&')}` : cleanBasePath;
  }

  public stripOnboardingParamsFromRoute(): Promise<boolean> {
    // Use current router URL and strip onboarding params
    const cleanUrl = this.stripOnboardingParamsFromUrl(this.router.url);
    // Only navigate if the URL changed
    if (cleanUrl !== this.router.url) {
      return this.router.navigateByUrl(cleanUrl);
    }
    return Promise.resolve(true);
  }

  public canAccessStep(step: OnboardingStep) {
    if (!smbHospitalityOnboardingChecklistStepConfiguration[step]) {
      // If no config is available, it's an internal step
      return true;
    }

    const { featureFlag, minimumPlanRequired, excludedCountries } = smbHospitalityOnboardingChecklistStepConfiguration[
      step
    ] as OnboardingChecklistStepConfiguration;

    if (featureFlag && !this.featureService.isFeatureActivated(featureFlag)) {
      return false;
    }

    if (minimumPlanRequired && !hasAtleastSubscriptionPlan(minimumPlanRequired, this.subscription)) {
      return false;
    }

    if (excludedCountries?.includes(this.account?.country)) {
      return false;
    }

    return true;
  }

  /**
   * Helper method used for mapping form state of the employees table used in onboarding-employees and invite-employees components to api payload(s).
   * Most updates are done through the batch endpoint, but since this endpoint doesn't support team updates for existing users, we need to do separate requests for these updates.
   */
  public mapEmployeeForBatchRequestAndUpdateTeamRequests(employee): {
    batchPayload: EmployeeForBatchPayload;
    updateTeamsPayload?: { employeeId: string; payload: EmployeeTeamSavePayload };
  } {
    const { removable, Team: newTeamId, ...rest } = employee;

    if (!employee.id) {
      return {
        batchPayload: {
          ...rest,
          team_id: newTeamId,
        },
      };
    }

    let updateTeamsPayload;
    const oldTeamId = this.employees.find((e) => e.id === employee.id)?.Team?.[0];

    if (newTeamId !== oldTeamId) {
      const oldTeamDepartment = this.teams[oldTeamId]?.department?.id;
      const newTeamDepartment = this.teams[newTeamId]?.department?.id;
      updateTeamsPayload = {
        employeeId: employee.id,
        payload: {
          [oldTeamDepartment]: null,
          [newTeamDepartment]: newTeamId,
        },
      };
    }

    return {
      batchPayload: rest,
      updateTeamsPayload,
    };
  }
}
